import cloneDeepWith from 'lodash/cloneDeepWith';

/**
 * List of keys that should have their values redacted from error logs.
 * These keys may contain sensitive authentication or personal data.
 */
const SENSITIVE_KEYS = [
    // Headers
    'authorization',
    'cookie',
    'set-cookie',
    'x-api-key',
    'x-auth-token',
    // Body fields
    'client_secret',
    'client_id',
    'password',
    'token',
    'access_token',
    'refresh_token',
    'api_key',
    'apikey',
    'secret',
    'credential',
    'credentials',
];

/**
 * Checks if a key is sensitive and should be redacted.
 *
 * @param {string} key - The key to check
 * @returns {boolean} True if the key is sensitive
 */
const isSensitiveKey = (key) => {
    if (typeof key !== 'string') return false;
    return SENSITIVE_KEYS.includes(key.toLowerCase());
};

/**
 * Sanitizes an object by redacting sensitive keys.
 *
 * @param {Object} obj - The object to sanitize
 * @returns {Object} Sanitized object
 */
const sanitizeObject = (obj) =>
    cloneDeepWith(obj, (value, key) => {
        // Handle XMLHttpRequest
        if (value?.constructor?.name === 'XMLHttpRequest') {
            return '[XMLHttpRequest REDACTED]';
        }

        // Redact sensitive keys (e.g., authorization, password, token, etc.)
        if (isSensitiveKey(key)) {
            return '[REDACTED]';
        }

        // Check for AxiosHeaders class name to redact headers from Axios requests
        if (value?.constructor?.name === 'AxiosHeaders') {
            return '[AxiosHeaders REDACTED]';
        }

        // Handle 'headers' objects - sanitize any sensitive keys within them
        if (key === 'headers' && value && typeof value === 'object') {
            const sanitizedHeaders = {};
            Object.keys(value).forEach((headerKey) => {
                if (isSensitiveKey(headerKey)) {
                    sanitizedHeaders[headerKey] = '[REDACTED]';
                } else {
                    sanitizedHeaders[headerKey] = value[headerKey];
                }
            });
            return sanitizedHeaders;
        }

        // Handle strings with sensitive data.
        // Except for "message" key since error messages are useful for debugging and typically
        // contain descriptive text rather than actual sensitive values.
        // Except for keys that include "url" since URLs may contain query parameters that are not sensitive
        // e.g. `https://api.splashthat.com/oauth/v2/token`
        if (
            typeof key === 'string' &&
            typeof value === 'string' &&
            !['url', 'message'].includes(key.toLowerCase()) &&
            SENSITIVE_KEYS.some((k) => value.toLowerCase().includes(k))
        ) {
            return '[SensitiveString REDACTED]';
        }

        // Return undefined to let cloneDeepWith handle it normally
        return undefined;
    });

/**
 * Sanitizes error objects before logging to prevent sensitive data leaks.
 * Removes sensitive data (Authorization, passwords, tokens, etc.) while preserving
 * the rest of the error structure for debugging purposes.
 *
 * @param {Error|Object} error - The error object to sanitize
 * @returns {Object} A sanitized error object safe for logging
 */
export const sanitizeError = (error) => {
    if (!error) {
        return { message: 'Unknown error' };
    }

    if (typeof error !== 'object') {
        return { message: String(error) };
    }

    const sanitized = sanitizeObject(error);

    // Ensure we always have a message
    if (!sanitized.message && error.message) {
        sanitized.message = error.message;
    }

    return sanitized;
};

/**
 * Creates a sanitized error handler for use with .catch()
 * Usage: .catch(createSanitizedErrorHandler(LOG, 'Error message'))
 *
 * @param {Object} logger - The logger instance
 * @param {string} message - The error message to log
 * @returns {Function} A function that logs sanitized errors
 */
export const createSanitizedErrorHandler = (logger, message) => (error) => {
    logger.error(message, sanitizeError(error));
};

export default sanitizeError;
